/**
 * TemplateShiftEditor - Interactive day view for editing template shifts
 * Allows users to visually create, edit, move, and delete shifts within a day template
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { format, set, addMinutes } from 'date-fns';
import { DayTemplateShift, DayShift, Employee } from '@/types';
import { organizeShiftsIntoRows, ShiftRow } from '@/lib/scheduling/shift-utils';
import { ROW_HEIGHT_PX, INTERVALS_PER_HOUR, INTERVAL_MINUTES } from '@/components/shifts/constants';
import HourCell from '@/components/shifts/HourCell';
import DraggableShift from '@/components/shifts/DraggableShift';
import ShiftCreationOverlay from '@/components/shifts/ShiftCreationOverlay';
import TimeHeader from '@/components/shifts/TimeHeader';
import TemplateShiftActionsPanel from './TemplateShiftActionsPanel';

interface TemplateShiftEditorProps {
  shifts: DayTemplateShift[];
  onShiftsChange: (shifts: DayTemplateShift[]) => void;
  displayStartHour?: number;
  displayEndHour?: number;
  businessId?: string | null;
  employees?: Employee[];
  saveEmployeeAssignments?: boolean;
}

const TemplateShiftEditor: React.FC<TemplateShiftEditorProps> = ({
  shifts,
  onShiftsChange,
  displayStartHour = 0,
  displayEndHour = 24,
  businessId,
  employees = [],
  saveEmployeeAssignments = false,
}) => {
  // Use today as a reference date for converting template shifts to display shifts
  const referenceDate = useMemo(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }, []);

  // State for shift creation
  const [creationStartTime, setCreationStartTime] = useState<Date | null>(null);
  const [creationEndTime, setCreationEndTime] = useState<Date | null>(null);
  const [creationRowIndex, setCreationRowIndex] = useState<number | null>(null);
  const [isResizeMode, setIsResizeMode] = useState(false);

  // State for shift selection
  const [selectedShiftIds, setSelectedShiftIds] = useState<Set<string>>(new Set());

  // Container measurement for dynamic width calculation
  const containerRef = useRef<HTMLDivElement>(null);
  const [measuredContainerWidth, setMeasuredContainerWidth] = useState<number>(0);
  const lastStableWidthRef = useRef<number>(0); // Track last stable width to prevent micro-fluctuations

  // Measure container width on mount and resize
  useEffect(() => {
    const measureContainer = () => {
      if (containerRef.current) {
        const width = Math.round(containerRef.current.offsetWidth);
        // Only update if change is significant (> 2px) to prevent width "bouncing"
        const widthDiff = Math.abs(width - lastStableWidthRef.current);
        if (widthDiff > 2 || lastStableWidthRef.current === 0) {
          lastStableWidthRef.current = width;
          setMeasuredContainerWidth(width);
        }
      }
    };

    measureContainer();

    // Set up resize observer for responsive width
    const resizeObserver = new ResizeObserver(measureContainer);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  /**
   * Convert DayTemplateShift (HH:MM string) to DayShift (Date objects)
   */
  const templateShiftToDayShift = useCallback((templateShift: DayTemplateShift, index: number): DayShift => {
    const [startHour, startMinute] = templateShift.startTime.split(':').map(Number);
    const [endHour, endMinute] = templateShift.endTime.split(':').map(Number);

    const startTime = set(referenceDate, { hours: startHour, minutes: startMinute, seconds: 0, milliseconds: 0 });
    let endTime = set(referenceDate, { hours: endHour, minutes: endMinute, seconds: 0, milliseconds: 0 });

    // Handle shifts that cross midnight
    if (endTime <= startTime) {
      endTime = new Date(endTime.getTime() + 24 * 60 * 60 * 1000);
    }

    return {
      id: `template-shift-${index}`,
      employeeName: templateShift.employeeName || '',
      employeeId: templateShift.employeeId || undefined,
      startTime,
      endTime,
      title: templateShift.title,
      breaks: templateShift.breaks,
      isNew: false,
    };
  }, [referenceDate]);

  /**
   * Convert DayShift (Date objects) back to DayTemplateShift (HH:MM string)
   */
  const dayShiftToTemplateShift = useCallback((dayShift: DayShift): DayTemplateShift => {
    const startTime = format(dayShift.startTime, 'HH:mm');
    const endTime = format(dayShift.endTime, 'HH:mm');

    return {
      startTime,
      endTime,
      title: dayShift.title || 'Shift',
      employeeRole: undefined,
      employeeId: saveEmployeeAssignments ? dayShift.employeeId : undefined,
      employeeName: saveEmployeeAssignments ? dayShift.employeeName : undefined,
      breaks: dayShift.breaks,
    };
  }, [saveEmployeeAssignments]);

  /**
   * Convert template shifts to display shifts for rendering
   */
  const displayShifts = useMemo(() => {
    return shifts.map((shift, index) => templateShiftToDayShift(shift, index));
  }, [shifts, templateShiftToDayShift]);

  /**
   * Organize shifts into rows for display
   */
  const organizedShifts = useMemo(() => {
    return organizeShiftsIntoRows(displayShifts);
  }, [displayShifts]);

  const numRows = Math.max(organizedShifts.length + 1, 3); // Always one extra empty row, minimum 3 total

  /**
   * Calculate display times
   */
  const effectiveDisplayStartTime = useMemo(() => {
    return set(referenceDate, { hours: displayStartHour, minutes: 0, seconds: 0, milliseconds: 0 });
  }, [referenceDate, displayStartHour]);

  const effectiveDisplayEndTime = useMemo(() => {
    return set(referenceDate, { hours: displayEndHour, minutes: 0, seconds: 0, milliseconds: 0 });
  }, [referenceDate, displayEndHour]);

  const totalDisplayHours = displayEndHour - displayStartHour;
  const totalIntervals = Math.ceil(totalDisplayHours * INTERVALS_PER_HOUR);

  // Calculate dynamic hour cell width based on measured container width
  const hourCellWidthPx = useMemo(() => {
    if (!measuredContainerWidth || measuredContainerWidth <= 0) {
      return 0; // Signal to skip positioning until measured
    }
    // Divide available width by number of intervals for responsive sizing
    return measuredContainerWidth / totalIntervals;
  }, [measuredContainerWidth, totalIntervals]);

  /**
   * Handle hour cell click - start creating a new shift
   */
  const handleHourCellClick = useCallback((cellTime: Date, event: React.MouseEvent, rowIndex: number) => {
    if (isResizeMode || creationStartTime) return;

    setCreationStartTime(cellTime);
    setCreationEndTime(addMinutes(cellTime, 60)); // Default 1 hour shift
    setCreationRowIndex(rowIndex);
    setIsResizeMode(true);
  }, [isResizeMode, creationStartTime]);

  /**
   * Handle shift creation finalization
   */
  const handleShiftCreationFinalize = useCallback(() => {
    if (!creationStartTime || !creationEndTime) return;

    const newTemplateShift: DayTemplateShift = {
      startTime: format(creationStartTime, 'HH:mm'),
      endTime: format(creationEndTime, 'HH:mm'),
      title: 'New Shift',
      breaks: [],
    };

    onShiftsChange([...shifts, newTemplateShift]);

    // Reset creation state
    setCreationStartTime(null);
    setCreationEndTime(null);
    setCreationRowIndex(null);
    setIsResizeMode(false);
  }, [creationStartTime, creationEndTime, shifts, onShiftsChange]);

  /**
   * Handle shift reposition (drag)
   */
  const handleShiftReposition = useCallback(async (shiftId: string, newStartTime: Date, newEndTime: Date) => {
    const shiftIndex = parseInt(shiftId.replace('template-shift-', ''));
    if (isNaN(shiftIndex)) return;

    const updatedShifts = [...shifts];
    updatedShifts[shiftIndex] = {
      ...updatedShifts[shiftIndex],
      startTime: format(newStartTime, 'HH:mm'),
      endTime: format(newEndTime, 'HH:mm'),
    };

    onShiftsChange(updatedShifts);
  }, [shifts, onShiftsChange]);

  /**
   * Handle shift deletion
   */
  const handleShiftDelete = useCallback((shiftId: string) => {
    const shiftIndex = parseInt(shiftId.replace('template-shift-', ''));
    if (isNaN(shiftIndex)) return;

    const updatedShifts = shifts.filter((_, index) => index !== shiftIndex);
    onShiftsChange(updatedShifts);

    // Clear selection for deleted shift
    setSelectedShiftIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(shiftId);
      return newSet;
    });
  }, [shifts, onShiftsChange]);

  /**
   * Handle employee assignment
   */
  const handleEmployeeAssignment = useCallback((shiftId: string, employeeId: string | null) => {
    const shiftIndex = parseInt(shiftId.replace('template-shift-', ''));
    if (isNaN(shiftIndex)) return;

    const updatedShifts = [...shifts];
    const employee = employees.find(emp => emp.id === employeeId);

    updatedShifts[shiftIndex] = {
      ...updatedShifts[shiftIndex],
      employeeId: employeeId || undefined,
      employeeName: employee ? `${employee.first_name} ${employee.last_name}` : undefined,
    };

    onShiftsChange(updatedShifts);
  }, [shifts, employees, onShiftsChange]);

  /**
   * Handle shift selection
   */
  const handleShiftSelect = useCallback((shiftId: string, selected: boolean) => {
    setSelectedShiftIds(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(shiftId);
      } else {
        newSet.delete(shiftId);
      }
      return newSet;
    });
  }, []);

  /**
   * Clear all selections
   */
  const handleClearSelection = useCallback(() => {
    setSelectedShiftIds(new Set());
  }, []);

  /**
   * Delete selected shifts
   */
  const handleDeleteSelected = useCallback(() => {
    const indicesToDelete = Array.from(selectedShiftIds).map(shiftId => {
      return parseInt(shiftId.replace('template-shift-', ''));
    }).filter(index => !isNaN(index));

    const updatedShifts = shifts.filter((_, index) => !indicesToDelete.includes(index));
    onShiftsChange(updatedShifts);

    // Clear selection after deletion
    setSelectedShiftIds(new Set());
  }, [shifts, selectedShiftIds, onShiftsChange]);

  // Grid template configuration - use fractional units for responsive sizing
  const gridTemplateColumns = `repeat(${totalIntervals}, 1fr)`;
  const gridTemplateRows = `repeat(${numRows}, ${ROW_HEIGHT_PX}px)`;
  const gridHeight = numRows * ROW_HEIGHT_PX;

  return (
    <div className="template-shift-editor border border-border rounded-lg overflow-hidden flex">
      {/* Grid container - left side */}
      <div ref={containerRef} className="flex-1 min-w-0 overflow-hidden" style={{ maxHeight: '500px' }}>
        {/* Time axis header */}
        <div
          className="relative bg-muted/20"
          style={{
            display: 'grid',
            gridTemplateColumns,
            height: '40px',
          }}
        >
          <TimeHeader
            totalDisplayHours={totalDisplayHours}
            effectiveDisplayStartTime={effectiveDisplayStartTime}
            hideDayColumn={true}
            show24Hours={true}
          />
        </div>

        {/* Editor grid */}
        <div
          className="relative bg-background"
          style={{
            display: 'grid',
            gridTemplateColumns,
            gridTemplateRows,
            height: `${gridHeight}px`,
          }}
        >
        {/* Hour cells */}
        {Array.from({ length: numRows }, (_, rowIndex) => {
          return Array.from({ length: totalIntervals }, (_, colIndex) => {
            const intervalStartTime = addMinutes(effectiveDisplayStartTime, colIndex * INTERVAL_MINUTES);
            const isFirstInterval = colIndex === 0;
            const isLastInterval = colIndex === totalIntervals - 1;
            // Check if this is an hour marker (at hour boundaries)
            const isHourMarker = colIndex % INTERVALS_PER_HOUR === 0;

            return (
              <HourCell
                key={`cell-${rowIndex}-${colIndex}`}
                cellTime={intervalStartTime}
                rowIndex={rowIndex}
                dayIndex={0}
                dayOfMonth={1}
                dayKey={format(referenceDate, 'yyyy-MM-dd')}
                isToday={false}
                isWeekendDay={false}
                isClosedAllDay={false}
                holiday={null}
                openingHoursByDayOfWeek={new Map()}
                isCurrentlyEditing={true}
                isEditingMode={true}
                show24Hours={false}
                isFirstInterval={isFirstInterval}
                isLastInterval={isLastInterval}
                isFirstRowInDay={rowIndex === 0}
                isLastRowInDay={rowIndex === numRows - 1}
                gridRowIndex={rowIndex + 1}
                gridColIndex={colIndex + 1}
                onTemplateCreationClick={handleHourCellClick}
                isTemplateCreationInProgress={Boolean(creationStartTime)}
                // Required props for HourCell
                isHourMarker={isHourMarker}
                rowHeightPx={ROW_HEIGHT_PX}
                hourCellWidthPx={hourCellWidthPx}
                businessId={undefined}
                onCreateShift={() => Promise.resolve()}
                onRepositionShift={() => Promise.resolve()}
                hideDayColumn={true}
              />
            );
          });
        })}

        {/* Shift layer */}
        {organizedShifts.map((row) =>
          row.shifts.map((shift) => (
            <DraggableShift
              key={shift.id}
              shift={shift as DayShift}
              rowIndex={row.rowIndex}
              dayIndex={0}
              employees={employees}
              businessId={businessId}
              onAssignEmployee={saveEmployeeAssignments ? handleEmployeeAssignment : undefined}
              onReposition={handleShiftReposition}
              onDelete={handleShiftDelete}
              displayStartTime={effectiveDisplayStartTime}
              displayEndTime={effectiveDisplayEndTime}
              totalDisplayHours={totalDisplayHours}
              hourCellWidthPx={hourCellWidthPx}
              hideDayColumn={true}
              selectedShiftIds={selectedShiftIds}
              onShiftSelect={handleShiftSelect}
            />
          ))
        )}

        {/* Shift creation overlay */}
        {creationStartTime && creationEndTime && creationRowIndex !== null && (
          <ShiftCreationOverlay
            startTime={creationStartTime}
            endTime={creationEndTime}
            effectiveDisplayStartTime={effectiveDisplayStartTime}
            effectiveDisplayEndTime={effectiveDisplayEndTime}
            totalDisplayHours={totalDisplayHours}
            calculatedHourCellWidthPx={hourCellWidthPx}
            numRows={numRows}
            rowHeightPx={ROW_HEIGHT_PX}
            intervalMinutes={INTERVAL_MINUTES}
            minutesPerHour={60}
            targetRowIndex={creationRowIndex}
            targetDayVerticalOffset={0}
            dayColumnOffset={0}
            isResizeMode={isResizeMode}
            onResizeComplete={(newStartTime, newEndTime) => {
              setCreationStartTime(newStartTime);
              setCreationEndTime(newEndTime);
            }}
            onFinalizedClick={handleShiftCreationFinalize}
          />
        )}
        </div>
      </div>

      {/* Actions panel - right side */}
      <TemplateShiftActionsPanel
        shifts={shifts}
        selectedShiftIds={selectedShiftIds}
        onShiftSelect={handleShiftSelect}
        onClearSelection={handleClearSelection}
        onDeleteSelected={handleDeleteSelected}
        employees={employees}
        businessId={businessId}
        selectedDate={referenceDate}
        allDayShifts={displayShifts}
        saveEmployeeAssignments={saveEmployeeAssignments}
        className="w-[9rem] flex-shrink-0"
      />
    </div>
  );
};

export default TemplateShiftEditor;
